<template>
    <div class="match-function">
        <div
            class="match-function-item"
            v-bind:class="{
                _disabled: !isStatisticsBtnShow,
                JARVIS_MatchCard_Btn_Statistics: isStatisticsBtnShow,
                JARVIS_MatchCard_Btn_Statistics_Disable: !isStatisticsBtnShow,
            }"
        >
            <div v-if="isStatisticsBtnShow" v-on:click="openStatisticsByUrl(match.statisticsUrl)">
                <SvgIcon iconName="sportIcon_statistics" />
            </div>
            <div v-else>
                <SvgIcon iconName="sportIcon_statistics_disabled" />
            </div>
        </div>
        <div
            v-if="!match.isLive && !isOutright && !isFromApp"
            class="match-function-item _calendar JARVIS_MatchCard_Btn_AddToCalendar"
            v-on:click="addToCalendar(match)"
        >
            <SvgIcon iconName="systemIcon_calendar" />
        </div>
        <div
            v-if="isLiveStreamBtnShow"
            class="match-function-item _video"
            v-bind:class="{
                JARVIS_MatchCard_Btn_LiveVideo: isLiveStreamBtnShow,
                JARVIS_MatchCard_Btn_LiveVideo_Disable: !isLiveStreamBtnShow,
            }"
            v-on:click="goToMatchDetail()"
        >
            {{ i18n.t('mtMatchDisplay_watchLive') }}
            <SvgIcon iconName="sportIcon_liveTv_disabled" />
        </div>
    </div>
</template>

<script lang="ts">
    import Cookies from 'js-cookie';
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, toRefs } from 'vue';
    import { useRouter } from 'vue-router';
    import { useLiveStream } from '@/components/liveStream/useLiveStream';
    import type { IMatch } from '@/interface/IMatch';
    import type { IMatchDetailRoute } from '@/interface/IMatchDetailRoute';
    import { MarketPage, RoutePage, SportType } from '@/interface/enum';
    import { useApp } from '@/libraries/composables/useApp';
    import { addToCalendar } from '@/libraries/helpers/calendarHelper';
    import { platform } from '@/libraries/helpers/platformHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { getHost, getSportsUrl } from '@/libraries/url';
    import { useAccountStore } from '@/stores/accountStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useProductPlatformStore } from '@/stores/productPlatformStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'MtMatchFunction',
        components: {
            SvgIcon,
        },
        props: {
            match: {
                type: Object as PropType<IMatch>,
                required: true,
            },
        },
        setup(props) {
            const { match } = toRefs(props);
            const { isLiveStreamPlayable } = useLiveStream(match);

            const { isAsi, marketPage } = storeToRefs(useRootStore());

            const { isComponentInPp } = storeToRefs(useProductPlatformStore());

            const { customerInfo } = storeToRefs(useAccountStore());

            const { isStatisticsEnabled } = storeToRefs(useFeatureToggleStore());

            const isStatisticsBtnShow = computed(() => isStatisticsEnabled.value
                && props.match.statisticsUrl
                && props.match.marketPage !== MarketPage.EarlyMarket);

            const isLiveStreamBtnShow = computed(() => isLiveStreamPlayable.value);

            const isOutright = computed(() => marketPage.value === MarketPage.Outright);

            function getMatchDetailRoute(matchFunctionTab: MarketPage) {
                const route = {
                    name: RoutePage.MatchDetail,
                    params: {
                        sportType: SportType.toRouteName(props.match.sportType),
                        leagueName: getTranslated(props.match.tournament.tournamentName).replace(/ /g, '-'),
                        matchName: `${getTranslated(props.match.homeTeam?.teamName).replace(/ /g, '-')}-vs-${getTranslated(props.match.awayTeam?.teamName).replace(/ /g, '-')}`,
                        matchId: props.match.eventId.toString(),
                    },
                    query: {},
                };

                if (matchFunctionTab) {
                    route.query = { mf: matchFunctionTab };
                }

                return route;
            }
            const { isFromApp } = useApp();

            const router = useRouter();
            function goToMatchDetail() {
                if (isAsi.value) {
                    router.push(getMatchDetailRoute(marketPage.value));
                } else {
                    setCookie(getMatchDetailRoute(marketPage.value));
                    platform.showLogin('Sports');
                }
            }

            function setCookie(matchDetailRoute: IMatchDetailRoute) {
                const threeMinute = new Date(new Date().getTime() + 3 * 60 * 1000);
                Cookies.set('toSportsRoute', JSON.stringify(matchDetailRoute), {
                    domain: getHost(),
                    expires: threeMinute,
                });
            }

            function openStatisticsByUrl(statisticsUrl: string) {
                const id = new URL(statisticsUrl).pathname.split('/').pop();
                if (!isAsi.value) {
                    platform.showLogin('Sports');
                    return;
                }

                const lang = i18n.locale.value.split('_')[0].toLowerCase() === 'th' ? 'th' : 'en';
                const url = `${getSportsUrl()}/mt-statistics.html?id=${id}&lang=${lang}`;
                window.open(
                    url,
                    'SBO Statistics',
                    'toolbar=no,scrollbars=no,resizable=no,location=no,width=1000,height=600',
                );
            }

            return {
                i18n,
                isLiveStreamPlayable,
                isAsi,
                marketPage,
                isComponentInPp,
                customerInfo,
                isStatisticsBtnShow,
                isLiveStreamBtnShow,
                isOutright,
                addToCalendar,
                openStatisticsByUrl,
                goToMatchDetail,
                isFromApp,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/mtMatchDisplay";
</style>
